"use client";

import { TextField, useFormFields } from "@payloadcms/ui";
import Link from "next/link";
import { TextFieldClient, TextFieldClientProps } from "payload";
import { useMemo } from "react";

const getBackgroundColor = (color?: string) => {
  switch (color) {
    case "dark":
      return "bg-[rgb(30, 28, 44)]";
    case "white":
      return "bg-white";
    case "orange":
      return "bg-orange-800";
    case "yellow":
      return "bg-yellow-500";
    case "blue":
      return "bg-blue-500";
    case "purple":
      return "bg-purple-500";
    default:
      return "bg-[rgb(30, 28, 44)]"; 
  }
};

const getTextColor = (color?: string) => {
  switch (color) {
    case "dark":
      return "text-black";
    case "white":
      return "text-white";
    case "orange":
      return "text-orange-500";
    case "yellow":
      return "text-yellow-500";
    case "blue":
      return "text-blue-500";
    case "purple":
      return "text-purple-500";
    default:
      return "text-white";
  }
};

export function CtaBoxAdminBlock({ field, path, validate }: TextFieldClientProps) {
  const titleField = useFormFields(([fields]) => fields["title"]);
  const title = titleField?.value as string | undefined;

  const bodyField = useFormFields(([fields]) => fields["body"]);
  const body = bodyField?.value as string | undefined;

  const buttonTextField = useFormFields(([fields]) => fields["buttonText"]);
  const buttonText = buttonTextField?.value as string | undefined;

  const buttonURLField = useFormFields(([fields]) => fields["buttonURL"]);
  const buttonURL = buttonURLField?.value as string | undefined;

  const backgroundColorField = useFormFields(([fields]) => fields["backgroundColor"]);
  const backgroundColor = backgroundColorField?.value as string | undefined;

  const textColorField = useFormFields(([fields]) => fields["textColor"]);
  const textColor = textColorField?.value as string | undefined;

  const enableBoldnessField = useFormFields(([fields]) => fields["enableBoldness"]);
  const enableBoldness = enableBoldnessField?.value as boolean | undefined;

  const props: TextFieldClient = useMemo<TextFieldClient>(
    () => ({
      ...field,
      type: "text",
      admin: {
        ...field.admin,
        label: "CTA Box",
        autoComplete: false,
        placeholder: "Your CTA Box",
        rtl: false,
      },
    }),
    [field]
  );

  return (
    <>
      <TextField field={props} path={path} validate={validate} />
      {title && body && buttonText && buttonURL && backgroundColor && textColor && (
        <CtaBoxBlock
          blockType="cta-box"
          title={title}
          body={body}
          buttonText={buttonText}
          buttonURL={buttonURL}
          backgroundColor={backgroundColor}
          textColor={textColor}
          enableBoldness={enableBoldness}
        />
      )}
    </>
  );
}

export type CtaBoxBlockProps = {
  blockType: "cta-box";
  title: string;
  body: string;
  buttonText: string;
  buttonURL: string;
  backgroundColor: string;
  textColor: string;
  enableBoldness?: boolean;
};

type Props = CtaBoxBlockProps & {
  className?: string;
};

export const CtaBoxBlock: React.FC<Props> = ({
  title,
  body,
  buttonText,
  buttonURL,
  backgroundColor,
  textColor,
  enableBoldness,
}: Props) => {
  const backgroundClass = getBackgroundColor(backgroundColor);
  const textColorClass = getTextColor(textColor);
  const fontWeightClass = enableBoldness ? "font-bold" : "font-normal";

  return (
    <div
      className={`my-9 flex flex-row items-center justify-between gap-1 rounded-lg border border-[rgb(66,66,66)] px-8 py-3 ${backgroundClass}`}
    >
      <div className="flex flex-col gap-2 py-2">
        <h2 className={`mb-0 mt-1 text-2xl ${fontWeightClass} ${textColorClass}`}>{title}</h2>
        <h3 className={`mb-1 text-xl font-normal ${textColorClass}`}>{body}</h3>
      </div>

      <Link
        href={buttonURL}
        className={`rounded-lg border-2 border-[rgb(76,54,109)] bg-[rgb(67,42,104)] px-8 py-4 text-2xl text-white no-underline transition-all ease-linear hover:border-[rgb(100,70,130)] ${fontWeightClass}`}
      >
        {buttonText}
      </Link>
    </div>
  );
};
